/**
 * KAN-980: чистые функции раздела "Платформа": разбор суммы ручной корректировки
 * кошелька, проверка формы, разбор ответа 409 о свежих аккаунтах, подписи строк пула.
 */
import type { FreshAccount, TgPoolAccount, TransferResultRow } from '@/services/api/platform';

export const WALLET_AMOUNT_ERROR = 'Сумма: число со знаком, не ноль, до двух знаков после запятой';
export const WALLET_COMMENT_ERROR = 'Укажите комментарий: он попадёт в журнал кошелька';

/**
 * Сумма со знаком из поля ввода строкой с двумя знаками: "-100,5" -> "-100.50",
 * "500" -> "500.00". Пусто, ноль или не число: null.
 */
export function normalizeSignedAmount(raw: string): string | null {
  const text = (raw ?? '').replace(/\s/g, '').replace(',', '.');
  const match = /^([+-]?)(\d+)(?:\.(\d{1,2}))?$/.exec(text);
  if (!match) return null;
  const [, sign, intPart, fracPart = ''] = match;
  const whole = intPart.replace(/^0+(?=\d)/, '');
  const fraction = fracPart.padEnd(2, '0');
  if (/^0+$/.test(whole) && /^0+$/.test(fraction)) return null;
  return `${sign === '-' ? '-' : ''}${whole}.${fraction}`;
}

/** Ошибка формы корректировки или null, если форму можно отправлять. */
export function validateWalletAdjustment(amount: string, comment: string): string | null {
  if (normalizeSignedAmount(amount) === null) return WALLET_AMOUNT_ERROR;
  if (!comment.trim()) return WALLET_COMMENT_ERROR;
  return null;
}

export interface FreshConflict {
  detail: string;
  fresh: FreshAccount[];
}

/** Ответ 409 requires_confirm (свежие аккаунты) из ошибки axios, иначе null. */
export function parseFreshConflict(err: unknown): FreshConflict | null {
  if (!err || typeof err !== 'object') return null;
  const response = (err as { response?: { status?: number; data?: unknown } }).response;
  if (response?.status !== 409 || !response.data || typeof response.data !== 'object') return null;
  const data = response.data as { detail?: unknown; requires_confirm?: unknown; fresh?: unknown };
  if (data.requires_confirm !== true) return null;
  return {
    detail: typeof data.detail === 'string' ? data.detail : '',
    fresh: Array.isArray(data.fresh) ? (data.fresh as FreshAccount[]) : [],
  };
}

/** Подпись аккаунта: телефон и метка. */
export function accountTitle(row: { phone: string | null; label: string | null; account_id: string }): string {
  return [row.phone, row.label].filter(Boolean).join(' ') || row.account_id;
}

export const TRANSFER_MOVED = 'moved';

/** Итог переноса по аккаунту: "Перенесён" или текст ошибки. */
export function transferOutcome(row: TransferResultRow): string {
  if (row.status === TRANSFER_MOVED) return 'Перенесён';
  return row.error || `Не перенесён (${row.status})`;
}

/** Строку можно выбрать: в режиме "Все аккаунты" аккаунты пула не выбираются. */
export function isAccountSelectable(account: TgPoolAccount, scope: 'pool' | 'all'): boolean {
  return scope === 'pool' || !account.in_pool;
}

/** Признаки аккаунта для колонки "Флаги": флаги шлюза и состояния записи. */
export function accountMarks(account: TgPoolAccount): string[] {
  const marks = [...(account.flags ?? [])];
  if (account.disabled) marks.push('выключен');
  if (account.decommissioned) marks.push('списан');
  if (account.personal) marks.push('личный');
  if (account.fresh) marks.push('свежий');
  return marks;
}

/** ISO-время в "дд.мм.гггг чч:мм" или "-". */
export function formatDateTime(value: string | null): string {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
